import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useBooking } from '../contexts/BookingContext';
import {
  db, doc, addDoc, updateDoc, getDoc, collection, serverTimestamp, increment
} from '../firebase/config';
import {
  ChevronLeft, CreditCard, MapPin, Check, AlertCircle, Shield,
  ShoppingBag, User, Package, Calendar, Clock, Wallet, Banknote,
  Smartphone, Copy, Info,
} from 'lucide-react';
import {
  DEFAULT_SHIPPING,
  DELIVERY_PROVIDERS,
  DELIVERY_TYPE_LABELS,
  getProviderRate,
  getProProviders,
  requiresAddress,
} from '../utils/deliveryProviders';

const PAYSTACK_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || '';

// ============================================================
// PAYMENT METHOD HELPERS
// ============================================================
const getBookingMethodsForPro = (proDoc) => {
  const pm = proDoc?.paymentMethods || {};
  const out = [];
  out.push({ id: 'card', label: 'Card', icon: 'card', note: 'Pay now via Paystack' });
  if (pm.acceptPayShap) {
    out.push({
      id: 'payshap',
      label: 'PayShap',
      icon: 'payshap',
      note: pm.payshapName ? `Send to ${pm.payshapName}` : 'Send a bank transfer',
    });
  }
  if (pm.acceptCash) {
    out.push({
      id: 'cash',
      label: 'Cash',
      icon: 'cash',
      note: 'Pay at appointment',
    });
  }
  return out;
};

const MethodIcon = ({ id, size = 14 }) => {
  if (id === 'cash') return <Banknote size={size} />;
  if (id === 'payshap') return <Smartphone size={size} />;
  return <CreditCard size={size} />;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();
  const { cartItems, cartTotal, clearCart, loading: cartLoading } = useCart();
  const {
    pendingBookings,
    clearBookings,
  } = useBooking();

  const [loading, setLoading] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});

  const [vendors, setVendors] = useState({});
  const [deliveryMethods, setDeliveryMethods] = useState({});
  const [bookingChoices, setBookingChoices] = useState({});

  const [address, setAddress] = useState({
    fullName: '',
    email: '',
    phone: '',
    line1: '',
    city: '',
    postalCode: '',
    country: 'South Africa',
  });

  const hasProducts = cartItems.length > 0;
  const hasBookings = pendingBookings.length > 0;

  // ---------- Redirect if both empty ----------
  useEffect(() => {
    if (!cartLoading && !hasProducts && !hasBookings) {
      navigate('/dashboard/shop');
    }
  }, [cartLoading, hasProducts, hasBookings, navigate]);

  useEffect(() => {
    if (!hasProducts && hasBookings) {
      setStep(2);
    }
  }, [hasProducts, hasBookings]);

  // ---------- Prefill address ----------
  useEffect(() => {
    if (userData) {
      setAddress(prev => ({
        ...prev,
        fullName: prev.fullName || userData.name || '',
        email: prev.email || userData.email || user?.email || '',
        phone: prev.phone || userData.phone || '',
        line1: prev.line1 || userData.shippingAddress?.line1 || userData.shippingAddress?.address || '',
        city: prev.city || userData.shippingAddress?.city || '',
        postalCode: prev.postalCode || userData.shippingAddress?.postalCode || '',
        country: prev.country || userData.shippingAddress?.country || 'South Africa',
      }));
    }
  }, [userData, user]);

  // ---------- Group products by vendor ----------
  const vendorsByItems = useMemo(() => {
    const map = new Map();
    cartItems.forEach(item => {
      const proId = item.professionalId || '__global__';
      if (!map.has(proId)) {
        map.set(proId, {
          proId,
          proName: item.professionalName || 'Skytone',
          proImage: item.professionalImage || null,
          items: [],
        });
      }
      map.get(proId).items.push(item);
    });
    return Array.from(map.values());
  }, [cartItems]);

  // ---------- Load vendor shipping + paymentMethods ----------
  useEffect(() => {
    let cancelled = false;

    const loadVendors = async () => {
      if (!hasProducts && !hasBookings) {
        setLoadingVendors(false);
        return;
      }
      setLoadingVendors(true);
      const result = {};

      // Load product vendors
      for (const v of vendorsByItems) {
        if (cancelled) return;

        if (v.proId === '__global__') {
          result[v.proId] = { ...v, shipping: { ...DEFAULT_SHIPPING } };
          continue;
        }

        try {
          const snap = await getDoc(doc(db, 'users', v.proId));
          if (snap.exists()) {
            const proData = snap.data();
            result[v.proId] = {
              ...v,
              proData,
              shipping: {
                ...DEFAULT_SHIPPING,
                ...(proData.shipping || {}),
                collectAddress:
                  proData.shipping?.collectAddress ||
                  proData.location ||
                  'Contact vendor for pickup',
              },
            };
          } else {
            result[v.proId] = { ...v, shipping: { ...DEFAULT_SHIPPING } };
          }
        } catch (err) {
          console.error('Failed to load vendor shipping:', err);
          result[v.proId] = { ...v, shipping: { ...DEFAULT_SHIPPING } };
        }
      }

      setVendors(result);

      if (!cancelled && hasProducts) {
        const defaults = {};
        Object.values(result).forEach(v => {
          const providers = getProProviders(v.shipping);
          defaults[v.proId] = providers[0]?.id || 'collect';
        });
        setDeliveryMethods(defaults);
      }

      // Load pro docs for bookings (to get paymentMethods)
      if (hasBookings) {
        const seenPros = new Set();
        const bookingProDocs = {};

        for (const b of pendingBookings) {
          if (cancelled) return;
          const proId = b.professionalId;
          if (!proId || seenPros.has(proId)) continue;
          seenPros.add(proId);
          try {
            const snap = await getDoc(doc(db, 'users', proId));
            bookingProDocs[proId] = snap.exists() ? snap.data() : null;
          } catch (err) {
            bookingProDocs[proId] = null;
          }
        }

        const choices = {};
        pendingBookings.forEach(b => {
          const proDoc = bookingProDocs[b.professionalId];
          const methods = getBookingMethodsForPro(proDoc);
          const defaultMethod =
            b.paymentPolicy === 'after'
              ? (methods.find(m => m.id !== 'card')?.id || 'card')
              : 'card';
          choices[b.id] = { method: defaultMethod, sent: false };
        });

        if (!cancelled) {
          setBookingChoices(choices);
          setVendors(prev => ({
            ...prev,
            ...Object.fromEntries(
              Object.entries(bookingProDocs).map(([id, doc]) => [`pro_${id}`, doc])
            ),
          }));
        }
      }

      setLoadingVendors(false);
    };

    loadVendors();
    return () => { cancelled = true; };
  }, [vendorsByItems, hasProducts, hasBookings, pendingBookings]);

  // ---------- Helpers ----------
  const getVendorSubtotal = (vendor) =>
    vendor.items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  const getVendorDeliveryFee = (vendor, providerId) => {
    const v = vendors[vendor.proId];
    if (!v) return 0;
    const vendorSubtotal = getVendorSubtotal(vendor);
    const threshold = v.shipping.freeShippingThreshold || 0;
    const provider = DELIVERY_PROVIDERS[providerId];
    if (provider?.type === 'door' && threshold > 0 && vendorSubtotal >= threshold) {
      return 0;
    }
    return getProviderRate(providerId, v.shipping);
  };

  const getBookingTotal = (b) =>
    Number(
      b.totalPrice ??
      ((b.price || 0) + Number(b.addonsTotal || 0) + (b.transportFee || 0))
    );

  const getBookingAmountForMethod = (b, methodId) => {
    const total = getBookingTotal(b);
    const policy = b.paymentPolicy || 'after';
    if (methodId === 'cash') return 0;
    if (policy === 'after') return 0;
    if (policy === 'full') return total;
    if (policy === 'deposit') {
      return Number(b.depositAmount || Math.round(total * ((b.depositPercent || 30) / 100)));
    }
    return 0;
  };

  const bookingGroups = useMemo(() => {
    const card = [];
    const payshap = [];
    const cash = [];
    pendingBookings.forEach(b => {
      const choice = bookingChoices[b.id] || { method: 'card' };
      const entry = { booking: b, method: choice.method };
      if (choice.method === 'payshap') payshap.push(entry);
      else if (choice.method === 'cash') cash.push(entry);
      else card.push(entry);
    });
    return { card, payshap, cash };
  }, [pendingBookings, bookingChoices]);

  const subtotal = cartTotal;

  const shippingTotal = vendorsByItems.reduce((sum, v) => {
    const method = deliveryMethods[v.proId];
    if (!method) return sum;
    return sum + getVendorDeliveryFee(v, method);
  }, 0);

  const productsTotalNow = subtotal + shippingTotal;

  const cardBookingsTotal = bookingGroups.card.reduce(
    (sum, { booking, method }) => sum + getBookingAmountForMethod(booking, method),
    0
  );

  const paystackTotal = productsTotalNow + cardBookingsTotal;

  const payshapTotal = bookingGroups.payshap.reduce(
    (sum, { booking, method }) => sum + getBookingAmountForMethod(booking, method),
    0
  );

  const bookingsGrandTotal = pendingBookings.reduce(
    (sum, b) => sum + getBookingTotal(b),
    0
  );

  const anyVendorNeedsAddress = vendorsByItems.some(v => {
    const method = deliveryMethods[v.proId];
    return method && requiresAddress(method);
  });

  // ---------- Validation ----------
  const validateDeliveryStep = () => {
    if (!hasProducts) return true;
    const missing = vendorsByItems.filter(v => !deliveryMethods[v.proId]);
    if (missing.length > 0) {
      setErrors({ delivery: 'Pick a delivery method for every vendor' });
      return false;
    }
    setErrors({});
    return true;
  };

  const validateAddressStep = () => {
    if (!anyVendorNeedsAddress) return true;
    const newErrors = {};
    if (!address.fullName.trim()) newErrors.fullName = 'Required';
    if (!address.email.trim()) newErrors.email = 'Required';
    else if (!/^\S+@\S+\.\S+$/.test(address.email)) newErrors.email = 'Invalid email';
    if (!address.phone.trim()) newErrors.phone = 'Required';
    if (!address.line1.trim()) newErrors.line1 = 'Required';
    if (!address.city.trim()) newErrors.city = 'Required';
    if (!address.postalCode.trim()) newErrors.postalCode = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateDeliveryStep()) setStep(2);
  };

  // ---------- Firestore helpers ----------
  const cleanForFirestore = (obj) => {
    if (Array.isArray(obj)) return obj.map(cleanForFirestore);
    if (obj && typeof obj === 'object' && obj.constructor === Object) {
      const out = {};
      Object.entries(obj).forEach(([k, v]) => {
        if (v !== undefined) out[k] = cleanForFirestore(v);
      });
      return out;
    }
    return obj;
  };

  const stripPendingFields = (b) => {
    const { id: _id, addedAt: _a, chosenMethod: _m, ...payload } = b;
    return payload;
  };

  // ---------- Create product orders ----------
  const createProductOrders = async (paymentReference) => {
    if (!hasProducts) return [];
    const orderPromises = vendorsByItems.map(vendor => {
      const providerId = deliveryMethods[vendor.proId];
      const provider = DELIVERY_PROVIDERS[providerId];
      const vendorSubtotal = getVendorSubtotal(vendor);
      const deliveryFee = getVendorDeliveryFee(vendor, providerId);
      const needsAddr = requiresAddress(providerId);

      const orderData = {
        clientId: user.uid,
        clientName: address.fullName,
        clientEmail: address.email,
        clientPhone: address.phone,

        vendorId: vendor.proId,
        vendorName: vendor.proName,
        vendorImage: vendor.proImage || null,

        items: vendor.items.map(item => ({
          id: item.id,
          name: item.name,
          brand: item.brand || '',
          price: item.price,
          quantity: item.quantity,
          image: item.image || null,
          professionalId: item.professionalId || null,
          professionalName: item.professionalName || null,
        })),

        subtotal: vendorSubtotal,
        deliveryFee,
        total: vendorSubtotal + deliveryFee,
        currency: 'ZAR',

        deliveryMethod: providerId,
        deliveryProvider: provider?.name || 'Delivery',
        deliveryEstimate: provider?.estimate || '',
        deliveryType: provider?.type || 'door',
        deliveryAddress: needsAddr ? { ...address } : null,
        collectAddress: !needsAddr
          ? (vendors[vendor.proId]?.shipping?.collectAddress || 'Contact vendor')
          : null,

        status: 'paid',
        fulfillmentStatus: 'pending',
        paymentMethod: 'paystack',
        paymentReference,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      return addDoc(collection(db, 'orders'), cleanForFirestore(orderData));
    });
    const refs = await Promise.all(orderPromises);
    return refs.map(r => r.id);
  };

  // ---------- Create booking docs ----------
  const createBookingDocs = async (paymentReference) => {
    if (!hasBookings) return;

    for (const { booking: b } of bookingGroups.card) {
      const policy = b.paymentPolicy || 'after';
      let paymentStatus = 'unpaid';
      let amountPaid = 0;
      let amountDue = getBookingTotal(b);

      if (policy === 'full') {
        paymentStatus = 'paid_in_full';
        amountPaid = getBookingTotal(b);
        amountDue = 0;
      } else if (policy === 'deposit') {
        paymentStatus = 'deposit_paid';
        amountPaid = getBookingAmountForMethod(b, 'card');
        amountDue = getBookingTotal(b) - amountPaid;
      }

      await addDoc(collection(db, 'bookings'), cleanForFirestore({
        ...stripPendingFields(b),
        clientId: user.uid,
        status: 'pending',
        paymentStatus,
        amountPaid,
        amountDue,
        paymentMethod: 'card',
        paymentReference,
        paidAt: paymentStatus !== 'unpaid' ? serverTimestamp() : null,
        createdAt: serverTimestamp(),
      }));
    }

    for (const { booking: b } of bookingGroups.payshap) {
      await addDoc(collection(db, 'bookings'), cleanForFirestore({
        ...stripPendingFields(b),
        clientId: user.uid,
        status: 'pending',
        paymentStatus: 'pending_transfer',
        amountPaid: 0,
        amountDue: getBookingTotal(b),
        paymentMethod: 'payshap',
        paymentReference: null,
        createdAt: serverTimestamp(),
      }));
    }

    for (const { booking: b } of bookingGroups.cash) {
      await addDoc(collection(db, 'bookings'), cleanForFirestore({
        ...stripPendingFields(b),
        clientId: user.uid,
        status: 'pending',
        paymentStatus: 'unpaid',
        amountPaid: 0,
        amountDue: getBookingTotal(b),
        paymentMethod: 'cash',
        paymentReference: null,
        createdAt: serverTimestamp(),
      }));
    }
  };

  // ---------- Decrement stock ----------
  const decrementStock = async () => {
    if (!hasProducts) return;
    const decrements = cartItems
      .filter(item => item.id)
      .map(item =>
        updateDoc(doc(db, 'products', item.id), {
          stock: increment(-item.quantity),
          updatedAt: serverTimestamp(),
        }).catch(err => {
          console.warn(`Stock decrement failed for ${item.id}:`, err);
        })
      );
    await Promise.allSettled(decrements);
  };

  // ---------- Post-payment success handler (shared) ----------
  const handleCheckoutSuccess = async (paymentReference) => {
    const orderIds = await createProductOrders(paymentReference);
    await decrementStock();
    await createBookingDocs(paymentReference);
    await clearBookings();
    await clearCart();

    if (orderIds.length > 0) {
      navigate(
        orderIds.length === 1
          ? `/order-confirmation/${orderIds[0]}`
          : `/order-confirmation/${orderIds.join(',')}`
      );
    } else {
      navigate('/dashboard/calendar');
    }
  };

  // ---------- Place order ----------
  const handlePlaceOrder = async () => {
    if (!user) { navigate('/login'); return; }
    if (!validateAddressStep()) return;

    // If nothing to charge via Paystack → skip popup entirely
    if (paystackTotal === 0) {
      setLoading(true);
      try {
        await createBookingDocs(null);
        await clearBookings();
        await clearCart();
        navigate('/dashboard/calendar');
      } catch (err) {
        console.error('Checkout (no-payment) failed:', err);
        alert('Could not save your bookings. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!PAYSTACK_KEY) {
      alert('Payment is not configured. Missing Paystack public key.\n\nAdd REACT_APP_PAYSTACK_PUBLIC_KEY to your .env and restart the dev server.');
      return;
    }

    // Defensive guard — Paystack V1 must be loaded
    if (!window.PaystackPop || typeof window.PaystackPop.setup !== 'function') {
      alert('Payment gateway is still loading. Please wait a moment and try again.');
      return;
    }

    setLoading(true);

    const amountInKobo = Math.round(paystackTotal * 100);
    const reference = `skytone_checkout_${Date.now()}_${user.uid.slice(0, 6)}`;

    try {
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_KEY,
        email: address.email,
        amount: amountInKobo,
        currency: 'ZAR',
        ref: reference,
        metadata: {
          custom_fields: [
            { display_name: 'Customer', variable_name: 'customer_name', value: address.fullName },
            { display_name: 'Bookings (card)', variable_name: 'card_bookings', value: bookingGroups.card.length },
            { display_name: 'Bookings (payshap)', variable_name: 'payshap_bookings', value: bookingGroups.payshap.length },
            { display_name: 'Bookings (cash)', variable_name: 'cash_bookings', value: bookingGroups.cash.length },
            { display_name: 'Products', variable_name: 'item_count', value: cartItems.length },
          ],
        },
        // IMPORTANT: callback MUST NOT be async — Paystack V1 rejects async functions.
        callback: (response) => {
          // Run async work in an IIFE so the outer function stays synchronous
          (async () => {
            try {
              await handleCheckoutSuccess(response.reference || reference);
            } catch (err) {
              console.error('Checkout save error:', err);
              alert('Payment succeeded but we could not save everything. Please contact support with reference: ' + (response.reference || reference));
            } finally {
              setLoading(false);
            }
          })();
        },
        onClose: () => { setLoading(false); },
      });

      if (!handler || typeof handler.openIframe !== 'function') {
        throw new Error('Paystack handler failed to initialize.');
      }

      handler.openIframe();
    } catch (err) {
      console.error('Paystack error:', err);
      alert('Could not open payment window: ' + (err?.message || 'Unknown error'));
      setLoading(false);
    }
  };

  // ---------- Render helpers ----------
  const renderMethodsForBooking = (b) => {
    const proDoc = vendors[`pro_${b.professionalId}`];
    const methods = getBookingMethodsForPro(proDoc);
    const current = bookingChoices[b.id]?.method || 'card';

    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {methods.map(m => {
          const selected = current === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setBookingChoices(prev => ({ ...prev, [b.id]: { ...prev[b.id], method: m.id } }))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                selected
                  ? 'bg-[#010101] text-white'
                  : 'bg-[#FCF8F9] text-gray-700 hover:bg-gray-100 border border-gray-100'
              }`}
            >
              <MethodIcon id={m.id} size={11} />
              {m.label}
            </button>
          );
        })}
      </div>
    );
  };

  const renderPayShapDetails = (b) => {
    const proDoc = vendors[`pro_${b.professionalId}`];
    const pm = proDoc?.paymentMethods || {};
    const amount = getBookingAmountForMethod(b, 'payshap');
    if (amount === 0) {
      return (
        <div className="mt-2 p-3 bg-[#FCF8F9] rounded-xl text-[11px] text-gray-500">
          No payment due now. You'll pay at the appointment.
        </div>
      );
    }
    return (
      <div className="mt-2 p-3 bg-[#F4EEFF] rounded-xl">
        <p className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider mb-1">
          Send {proDoc?.businessName || b.professionalName}
        </p>
        <p className="text-xs font-bold text-gray-900">
          R{amount.toFixed(2)} via PayShap
        </p>
        {pm.payshapId && (
          <p className="text-[11px] text-gray-600 mt-1 font-mono">
            {pm.payshapId}
            {pm.payshapName && <span className="ml-2 font-sans">({pm.payshapName})</span>}
          </p>
        )}
        {pm.payshapNote && (
          <p className="text-[11px] text-gray-500 mt-1 italic">{pm.payshapNote}</p>
        )}
        <p className="text-[10px] text-gray-500 mt-2">
          After sending, tap "Place Order". The pro will confirm receipt.
        </p>
      </div>
    );
  };

  // ---------- Loading ----------
  if (cartLoading || loadingVendors) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ---------- Empty ----------
  if (!hasProducts && !hasBookings) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <div className="w-24 h-24 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
          <ShoppingBag size={40} className="text-[#EFA9B6]" />
        </div>
        <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Your bag is empty</h2>
        <p className="text-gray-400 mb-6">Add items or bookings before checking out</p>
        <button
          onClick={() => navigate('/dashboard/shop')}
          className="px-6 py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-colors"
        >
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto pb-20">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-50 rounded-full transition-colors"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-3xl font-serif font-bold text-gray-900">Checkout</h1>
      </div>

      {hasProducts && (
        <div className="flex items-center gap-4 mb-8">
          <div className={`flex-1 flex items-center gap-3 ${step >= 1 ? 'text-[#EFA9B6]' : 'text-gray-300'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-[#EFA9B6] text-white' : 'bg-gray-200'}`}>
              {step > 1 ? <Check size={14} /> : '1'}
            </div>
            <span className="text-sm font-medium">Delivery</span>
          </div>
          <div className="flex-1 h-px bg-gray-200"></div>
          <div className={`flex-1 flex items-center gap-3 ${step >= 2 ? 'text-[#EFA9B6]' : 'text-gray-300'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-[#EFA9B6] text-white' : 'bg-gray-200'}`}>
              2
            </div>
            <span className="text-sm font-medium">Payment</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">

          {/* STEP 1 — DELIVERY */}
          {hasProducts && step === 1 && (
            <>
              {vendorsByItems.map((vendor) => {
                const v = vendors[vendor.proId] || { shipping: DEFAULT_SHIPPING };
                const providers = getProProviders(v.shipping);
                const vendorSubtotal = getVendorSubtotal(vendor);
                const chosen = deliveryMethods[vendor.proId];

                const groups = providers.reduce((acc, p) => {
                  const label = DELIVERY_TYPE_LABELS[p.type]?.label || 'Other';
                  if (!acc[label]) acc[label] = [];
                  acc[label].push(p);
                  return acc;
                }, {});

                return (
                  <div key={vendor.proId} className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                    <div className="p-5 border-b border-gray-50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#F4EEFF] flex items-center justify-center overflow-hidden shrink-0">
                        {vendor.proImage ? (
                          <img src={vendor.proImage} alt={vendor.proName} className="w-full h-full object-cover" />
                        ) : (
                          <User size={18} className="text-[#AF8BD4]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Vendor</p>
                        <p className="font-bold text-gray-900 truncate">{vendor.proName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Subtotal</p>
                        <p className="font-bold text-gray-900">R{vendorSubtotal.toFixed(2)}</p>
                      </div>
                    </div>

                    <div className="px-5 py-3 bg-[#FCF8F9] flex items-center gap-2 overflow-x-auto">
                      {vendor.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white rounded-full pl-1 pr-3 py-1 shrink-0">
                          <div className="w-6 h-6 rounded-full bg-[#F4EEFF] flex items-center justify-center overflow-hidden shrink-0">
                            {item.image ? (
                              <img src={item.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Package size={12} className="text-[#AF8BD4]" />
                            )}
                          </div>
                          <span className="text-xs font-medium text-gray-700 whitespace-nowrap">
                            {item.name} × {item.quantity}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="p-5 space-y-4">
                      {Object.entries(groups).map(([groupLabel, providersInGroup]) => (
                        <div key={groupLabel}>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">
                            {groupLabel}
                          </p>
                          <div className="space-y-2">
                            {providersInGroup.map((provider) => {
                              const fee = getVendorDeliveryFee(vendor, provider.id);
                              const isFree = fee === 0;
                              return (
                                <button
                                  key={provider.id}
                                  type="button"
                                  onClick={() => setDeliveryMethods(prev => ({ ...prev, [vendor.proId]: provider.id }))}
                                  className={`w-full text-left p-4 rounded-2xl border transition-colors flex items-start gap-3 ${
                                    chosen === provider.id
                                      ? 'border-[#EFA9B6] bg-[#FFF5F6]'
                                      : 'border-gray-100 hover:border-[#FBE1E8]'
                                  }`}
                                >
                                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                                    chosen === provider.id ? 'bg-[#EFA9B6] text-white' : 'bg-[#FCF8F9] text-gray-500'
                                  }`}>
                                    {provider.icon}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-bold text-sm text-gray-900">{provider.name}</p>
                                      {provider.type === 'pickup' && (
                                        <span className="text-[9px] font-bold text-green-600 uppercase tracking-wider px-2 py-0.5 bg-green-50 rounded-full">
                                          Free
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                                      {provider.description} · {provider.estimate}
                                    </p>
                                  </div>
                                  <div className="text-right shrink-0 ml-2">
                                    <p className={`font-bold text-sm ${isFree ? 'text-green-600' : 'text-gray-900'}`}>
                                      {isFree ? 'Free' : `R${fee.toFixed(2)}`}
                                    </p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}

                      {v.shipping.freeShippingThreshold > 0 &&
                       vendorSubtotal < v.shipping.freeShippingThreshold && (
                        <div className="flex items-start gap-2 p-3 bg-[#F4EEFF] rounded-xl">
                          <AlertCircle size={14} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                          <p className="text-[11px] text-gray-600 leading-relaxed">
                            Spend <span className="font-bold text-gray-900">R{(v.shipping.freeShippingThreshold - vendorSubtotal).toFixed(2)}</span> more to get free door delivery from this vendor.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {errors.delivery && (
                <div className="bg-red-50 border border-red-100 rounded-2xl p-3 flex items-start gap-2">
                  <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-600">{errors.delivery}</p>
                </div>
              )}

              <button
                onClick={handleContinue}
                className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors"
              >
                Continue to Payment
              </button>
            </>
          )}

          {/* STEP 2 — BOOKINGS + ADDRESS + PAYMENT */}
          {step === 2 && (
            <>
              {hasProducts && (
                <div className="bg-white rounded-3xl border border-gray-100 p-5">
                  <h2 className="text-lg font-serif font-bold text-gray-900 mb-4">Delivery Summary</h2>
                  <div className="space-y-3">
                    {vendorsByItems.map(vendor => {
                      const method = deliveryMethods[vendor.proId];
                      const provider = DELIVERY_PROVIDERS[method];
                      const fee = getVendorDeliveryFee(vendor, method);
                      return (
                        <div key={vendor.proId} className="flex items-center gap-3 text-sm">
                          <div className="w-8 h-8 rounded-full bg-[#F4EEFF] flex items-center justify-center shrink-0 overflow-hidden">
                            {vendor.proImage ? (
                              <img src={vendor.proImage} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <User size={14} className="text-[#AF8BD4]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900 truncate">{vendor.proName}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {provider?.name} · {provider?.estimate}
                            </p>
                          </div>
                          <span className="font-bold text-gray-900 shrink-0">
                            {fee === 0 ? 'Free' : `R${fee.toFixed(2)}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    className="text-xs text-[#EFA9B6] font-bold mt-4 hover:underline"
                  >
                    Edit delivery
                  </button>
                </div>
              )}

              {hasBookings && (
                <div className="bg-white rounded-3xl border border-gray-100 p-5">
                  <h2 className="text-lg font-serif font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar size={18} className="text-[#EFA9B6]" />
                    Bookings ({pendingBookings.length})
                  </h2>
                  <div className="space-y-5">
                    {pendingBookings.map(b => {
                      const total = getBookingTotal(b);
                      const policy = b.paymentPolicy || 'after';
                      const choice = bookingChoices[b.id]?.method || 'card';
                      const amountNow = getBookingAmountForMethod(b, choice);

                      return (
                        <div key={b.id} className="pb-5 border-b border-gray-50 last:border-0 last:pb-0">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#FFF5F6] flex items-center justify-center shrink-0 overflow-hidden">
                              {b.professionalImage ? (
                                <img src={b.professionalImage} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <User size={16} className="text-[#EFA9B6]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm text-gray-900 truncate">
                                {b.serviceTitle}
                                {b.optionName && <span className="text-[#AF8BD4]"> · {b.optionName}</span>}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{b.professionalName}</p>
                              <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1">
                                <Calendar size={10} />
                                <span>
                                  {new Date(b.date).toLocaleDateString('en-ZA', {
                                    weekday: 'short', day: 'numeric', month: 'short',
                                  })}
                                </span>
                                <Clock size={10} />
                                <span>{b.time}</span>
                              </div>
                              <div className="flex items-center gap-1.5 mt-1">
                                {policy === 'full' && <Wallet size={10} className="text-[#AF8BD4]" />}
                                {policy === 'deposit' && <CreditCard size={10} className="text-[#AF8BD4]" />}
                                {policy === 'after' && <Banknote size={10} className="text-[#AF8BD4]" />}
                                <span className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider">
                                  {policy === 'full' && 'Full upfront'}
                                  {policy === 'deposit' && `${b.depositPercent || 30}% deposit`}
                                  {policy === 'after' && 'Pay after'}
                                </span>
                              </div>
                            </div>
                            <div className="text-right shrink-0 ml-2">
                              <p className="font-bold text-sm text-gray-900">R{total.toFixed(2)}</p>
                              <p className="text-[10px] text-gray-400">total</p>
                            </div>
                          </div>

                          <div className="mt-3">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                              Payment
                            </p>
                            {renderMethodsForBooking(b)}

                            {choice === 'payshap' && renderPayShapDetails(b)}

                            {choice === 'cash' && (
                              <div className="mt-2 p-3 bg-[#FCF8F9] rounded-xl text-[11px] text-gray-500">
                                Bring R{total.toFixed(2)} to your appointment.
                              </div>
                            )}

                            {choice === 'card' && amountNow > 0 && (
                              <div className="mt-2 p-3 bg-[#FFF5F6] rounded-xl text-[11px] text-gray-600">
                                <span className="font-bold text-gray-900">
                                  R{amountNow.toFixed(2)}
                                </span>{' '}
                                will be charged now via Paystack.
                                {policy === 'deposit' && (
                                  <> Balance R{(total - amountNow).toFixed(2)} at appointment.</>
                                )}
                              </div>
                            )}

                            {choice === 'card' && amountNow === 0 && (
                              <div className="mt-2 p-3 bg-[#FCF8F9] rounded-xl text-[11px] text-gray-500">
                                No payment due now — this pro collects at the appointment.
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {anyVendorNeedsAddress && (
                <div className="bg-white rounded-3xl border border-gray-100 p-5">
                  <h2 className="text-lg font-serif font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin size={18} className="text-[#EFA9B6]" />
                    Delivery Address
                  </h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Field label="Full Name *" value={address.fullName} error={errors.fullName} onChange={(v) => setAddress(a => ({ ...a, fullName: v }))} placeholder="John Doe" />
                      <Field label="Email *" type="email" value={address.email} error={errors.email} onChange={(v) => setAddress(a => ({ ...a, email: v }))} placeholder="you@example.com" />
                    </div>
                    <Field label="Phone *" type="tel" value={address.phone} error={errors.phone} onChange={(v) => setAddress(a => ({ ...a, phone: v }))} placeholder="+27 82 123 4567" />
                    <Field label="Street Address *" value={address.line1} error={errors.line1} onChange={(v) => setAddress(a => ({ ...a, line1: v }))} placeholder="123 Main Street" />
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="City *" value={address.city} error={errors.city} onChange={(v) => setAddress(a => ({ ...a, city: v }))} placeholder="Cape Town" />
                      <Field label="Postal Code *" value={address.postalCode} error={errors.postalCode} onChange={(v) => setAddress(a => ({ ...a, postalCode: v }))} placeholder="8001" />
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-3xl border border-gray-100 p-5">
                <h2 className="text-lg font-serif font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Shield size={18} className="text-[#EFA9B6]" />
                  Payment Summary
                </h2>

                <div className="space-y-2 text-sm">
                  {hasProducts && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Products + delivery (via card)</span>
                      <span className="font-bold text-gray-900">R{productsTotalNow.toFixed(2)}</span>
                    </div>
                  )}
                  {cardBookingsTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {bookingGroups.card.length} booking{bookingGroups.card.length === 1 ? '' : 's'} (via card)
                      </span>
                      <span className="font-bold text-gray-900">R{cardBookingsTotal.toFixed(2)}</span>
                    </div>
                  )}
                  {payshapTotal > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {bookingGroups.payshap.length} booking{bookingGroups.payshap.length === 1 ? '' : 's'} (PayShap)
                      </span>
                      <span className="font-bold text-[#AF8BD4]">R{payshapTotal.toFixed(2)} — send manually</span>
                    </div>
                  )}
                  {bookingGroups.cash.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">
                        {bookingGroups.cash.length} booking{bookingGroups.cash.length === 1 ? '' : 's'} (cash)
                      </span>
                      <span className="font-bold text-gray-500">Pay at appointment</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-gray-100">
                    <span className="font-bold text-gray-900">
                      {paystackTotal > 0 ? 'Charged now via card' : 'Nothing to pay now'}
                    </span>
                    <span className="font-bold text-[#EFA9B6] text-lg">
                      R{paystackTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                {hasProducts && (
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-4 rounded-full bg-gray-50 text-gray-600 font-bold text-sm hover:bg-gray-100 transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  className="flex-1 bg-[#010101] text-white py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Processing...
                    </>
                  ) : paystackTotal > 0 ? (
                    <>
                      <CreditCard size={18} /> Pay R{paystackTotal.toFixed(2)} & Send
                    </>
                  ) : (
                    <>
                      <Check size={18} /> Place Order
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-3xl border border-gray-100 p-6 lg:sticky lg:top-24">
            <h3 className="text-lg font-serif font-bold text-gray-900 mb-4">Order Summary</h3>

            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
              {vendorsByItems.map(vendor => (
                <div key={vendor.proId}>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                    {vendor.proName}
                  </p>
                  {vendor.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-[#FCF8F9] flex items-center justify-center overflow-hidden shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <ShoppingBag size={16} className="text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{item.name}</p>
                        <p className="text-[10px] text-gray-400">× {item.quantity}</p>
                      </div>
                      <span className="font-bold text-xs text-gray-900">
                        R{((item.price || 0) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}

              {hasBookings && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                    Bookings
                  </p>
                  {pendingBookings.map(b => {
                    const total = getBookingTotal(b);
                    const choice = bookingChoices[b.id]?.method || 'card';
                    return (
                      <div key={b.id} className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-[#FFF5F6] flex items-center justify-center overflow-hidden shrink-0">
                          {b.professionalImage ? (
                            <img src={b.professionalImage} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Calendar size={16} className="text-[#EFA9B6]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">{b.serviceTitle}</p>
                          <p className="text-[10px] text-gray-400 capitalize">{choice}</p>
                        </div>
                        <span className="font-bold text-xs text-gray-900">
                          R{total.toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2 pt-4 border-t border-gray-50">
              {hasProducts && (
                <>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Products</span>
                    <span>R{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Delivery</span>
                    <span>{shippingTotal === 0 ? 'Free' : `R${shippingTotal.toFixed(2)}`}</span>
                  </div>
                </>
              )}
              {cardBookingsTotal > 0 && (
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Card bookings</span>
                  <span>R{cardBookingsTotal.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-50">
                <span>Charged now</span>
                <span>R{paystackTotal.toFixed(2)}</span>
              </div>
              {payshapTotal > 0 && (
                <p className="text-[10px] text-[#AF8BD4] pt-1">
                  +R{payshapTotal.toFixed(2)} to send via PayShap
                </p>
              )}
              {bookingGroups.cash.length > 0 && (
                <p className="text-[10px] text-gray-400 pt-1">
                  +R{(bookingsGrandTotal - cardBookingsTotal - payshapTotal).toFixed(2)} payable in cash
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, type = 'text', value, error, onChange, placeholder }) => (
  <div>
    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1.5">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full bg-[#FCF8F9] border ${
        error ? 'border-red-400' : 'border-gray-100'
      } rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6] transition-colors`}
    />
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

export default CheckoutPage;